/**
 * Emit hosts for the `session` analytics group — 21 functions
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import { getDemoModeForSession } from '@/features/demo-flow/utils/getDemoModeForSession';
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent, type TrackingProps } from '@/lib/analytics/track-legacy-event';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  type CaptureMode,
  type InputDefaultKind,
} from '@/models/analytics/features/audio';
import { type NoteEditorType } from '@/models/analytics/features/notes';
import {
  type CopyMethod,
  type CopySettingsSnapshot,
  type SessionCopiedEventProps,
  type SessionCopiedFormat,
  type SessionCopySource,
} from '@/models/analytics/features/sessions';
import { SegmentActionName, SegmentObjectName } from '@/models/analytics/segment-names';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { type CopyFormattingLoss } from '@heidi/editor/core';
import { getCurrentSessionId } from '@heidi/runtime/shell';
import { type LanguageCode, type VoiceStyle } from '@heidi/schema/ml-scribe';
import {
  GET_SESSION_BY_ID,
  type SessionResponse,
  type SessionUpdate,
} from '@heidi/session/contracts';

export function chooseDictation(sessionId: string) {
  // NOTE: This fires whenever the session's transcribe mode is set to
  // DICTATE — including automatic paths (HealthDirect default, upload
  // audio dialog), not just user-initiated dropdown clicks. It must NOT
  // dispatch `transcription_started`; that event only fires once
  // recording actually begins (see `sessionStartRecording`).
  // The explicit "user picked Dictate in the start dropdown" signal is
  // tracked as a `button_clicked` event from `StartButtonDropdown`.
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionDictation,
      sessionId: sessionId,
    },
  });
}

export function chooseTranscribe() {
  // See `chooseDictation` above for why `transcription_started` is not
  // dispatched here.
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionTranscribe,
    },
  });
}

export function reachedAudioLevelDuringRecording({
  sessionId,
  audioLevel,
}: {
  sessionId: string;
  audioLevel: NonNullable<TrackingProps['audioLevel']>;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.ReachedAudioLevelDuringRecording,
      audioLevel,
      sessionId,
    },
  });
}

export function selectLanguage(input: {
  selectLanguage: LanguageCode;
  multipleLanguages?: LanguageCode[];
  outputLanguage?: SessionUpdate['language_code'];
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionChangeLanguage,
      language: input.selectLanguage,
      multipleLanguages: input.multipleLanguages,
      outputLanguage: input.outputLanguage,
    },
  });
}

export function sessionAllDeleted() {
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionAllDeleted,
    },
  });

  // V2 — Amplitude + Datadog only
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.SessionDeleted,
    props: {
      delete_action: 'all_deleted',
    },
  });
}

export function sessionChooseMicInput(microphone: string) {
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionChooseMicInput,
      microphone: microphone,
    },
  });

  // V2 — Amplitude + Datadog only
  const sessionId = getCurrentSessionId();
  if (sessionId) {
    track({
      options: { amplitude: false },
      event_name: TrackingEventName.SessionUpdated,
      props: {
        session_id: sessionId,
        update_action: 'mic_input_changed',
        microphone,
      },
    });
  }
}

/**
 * Map the detector's camelCase flags onto the `formatting_removed` wire keys.
 * Picks the four booleans by name, so nothing else on the object can leak into
 * the payload.
 */
const toFormattingRemoved = (
  loss: CopyFormattingLoss
): NonNullable<SessionCopiedEventProps['formatting_removed']> => ({
  headings: loss.headings,
  bold_and_italics: loss.boldAndItalics,
  bullet_points: loss.bulletPoints,
  tables: loss.tables,
});

export function sessionCopyClicked(input: {
  format: SessionCopiedFormat;
  copyMethod: CopyMethod;
  selectedWordCount: number;
  totalWordCount: number;
  /** Which affordance the copy came from. */
  copySource: SessionCopySource;
  /**
   * Whether a `text/html` sibling accompanied `text/plain`. Required
   * because every emit site already branches on it — `format` alone
   * cannot express a toggle copy that wrote plain text only.
   */
  htmlFlavorWritten: boolean;
  /** Toggle state at copy time, on every format. */
  copySettings?: CopySettingsSnapshot;
  /** Editor variant, so copies are segmentable by editor. */
  editorType?: NoteEditorType;
  /**
   * The shared detector's result for this completed copy. Omit it wherever
   * the detector did not run; `formatting_removed` is then left off.
   */
  formattingLoss?: CopyFormattingLoss;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionCopyClicked,
    },
  });

  const percentageCopied =
    input.totalWordCount > 0
      ? Math.min(100, Math.round((input.selectedWordCount / input.totalWordCount) * 100))
      : 0;

  const sessionId = getCurrentSessionId();
  const sessionCopiedProps: SessionCopiedEventProps = {
    ...(sessionId != null && { session_id: sessionId }),
    format: input.format,
    copy_method: input.copyMethod,
    selected_word_count: input.selectedWordCount,
    total_word_count: input.totalWordCount,
    percentage_copied: percentageCopied,
    copy_source: input.copySource,
    html_flavor_written: input.htmlFlavorWritten,
    ...(input.copySettings != null && { copy_settings: input.copySettings }),
    ...(input.editorType != null && { editor_type: input.editorType }),
    ...(input.formattingLoss != null && {
      formatting_removed: toFormattingRemoved(input.formattingLoss),
    }),
  };

  track({
    options: { amplitude: false },
    event_name: TrackingEventName.SessionCopied,
    props: sessionCopiedProps,
  });
}

/** @deprecated use trackSessionCreated instead */
export function sessionCreated(sessionId: string) {
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionCreate,
      sessionId: sessionId,
    },
  });
}

export function sessionDateTimeUpdated(input: {
  sessionId: string;
  createdTimeLocal: string;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionDateTimeUpdated,
      sessionId: input.sessionId,
      createdTimeLocal: input.createdTimeLocal,
    },
  });

  // V2 — Amplitude + Datadog only
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.SessionUpdated,
    props: {
      session_id: input.sessionId,
      update_action: 'date_time_updated',
      created_time_local: input.createdTimeLocal,
    },
  });
}

// sessionBatchDeleted() {
//   dispatch.analyticsScribe.trackEvent({
//     name: SegmentObjectName.Session,
//     props: {
//       action: SegmentActionName.SessionBatchDeleted,
//     },
//   });
// },
export function sessionDeleteByIds(input?: { sessionCount?: number }) {
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionBatchDeleted,
    },
  });

  // V2 — Amplitude + Datadog only
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.SessionDeleted,
    props: {
      delete_action: 'batch_deleted',
      ...(input?.sessionCount !== undefined && {
        session_count: input.sessionCount,
      }),
    },
  });
}

export function sessionPauseRecording(input: {
  sessionId: string;
  isRecordingV2Enabled?: boolean;
  isTabSharing?: boolean;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionPauseRecording,
      sessionId: input.sessionId,
      isRecordingV2Enabled: input.isRecordingV2Enabled,
      isTabSharing: input.isTabSharing,
    },
  });

  // V2 — Amplitude + Datadog only
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TranscriptionStopped,
    props: {
      session_id: input.sessionId,
      is_recording_v2_enabled: input.isRecordingV2Enabled,
      is_tab_sharing: input.isTabSharing,
      demo_mode: getDemoModeForSession(input.sessionId),
    },
  });
}

export function sessionResumeRecording(input: {
  sessionId: string;
  isRecordingV2Enabled?: boolean;
  isTabSharing?: boolean;
  /** desktop speaker capture mode (shared with `capture_summary`). */
  captureMode?: CaptureMode;
  /** how the desktop mic selection resolved (explicit / console_default / comms_default). */
  inputDefaultKind?: InputDefaultKind;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionResumeRecording,
      sessionId: input.sessionId,
      isRecordingV2Enabled: input.isRecordingV2Enabled,
      isTabSharing: input.isTabSharing,
    },
  });

  // V2 — Amplitude + Datadog only
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TranscriptionStarted,
    props: {
      session_id: input.sessionId,
      is_resume: true,
      is_recording_v2_enabled: input.isRecordingV2Enabled,
      is_tab_sharing: input.isTabSharing,
      demo_mode: getDemoModeForSession(input.sessionId),
      // desktop capture telemetry — undefined on web paths.
      capture_mode: input.captureMode,
      input_default_kind: input.inputDefaultKind,
    },
  });
}

export function sessionStartRecording(input: {
  sessionId: string;
  transcribeMode: SessionResponse['transcribe_mode'];
  device?: string;
  deviceId?: string;
  isRecordingV2Enabled?: boolean;
  isTabSharing?: boolean;
  transcriptionServiceVersion?: 'v1' | 'v2';
  /** desktop speaker capture mode (shared with `capture_summary`). */
  captureMode?: CaptureMode;
  /** how the desktop mic selection resolved (explicit / console_default / comms_default). */
  inputDefaultKind?: InputDefaultKind;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionStartRecording,
      sessionId: input.sessionId,
      transcribeMode: input.transcribeMode,
      inputDevice: input.device,
      inputDeviceId: input.deviceId,
      isRecordingV2Enabled: input.isRecordingV2Enabled,
      isTabSharing: input.isTabSharing,
      transcriptionServiceVersion: input.transcriptionServiceVersion,
    },
  });

  // V2 — Amplitude + Datadog + Snowplow (via `trackEventV2`)
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TranscriptionStarted,
    props: {
      session_id: input.sessionId,
      transcribe_mode: input.transcribeMode ?? undefined,
      input_device: input.device,
      input_device_id: input.deviceId,
      is_recording_v2_enabled: input.isRecordingV2Enabled,
      is_tab_sharing: input.isTabSharing,
      // tag the event when this session is the user's shortened demo.
      demo_mode: getDemoModeForSession(input.sessionId),
      // desktop capture telemetry — undefined on web paths.
      capture_mode: input.captureMode,
      input_default_kind: input.inputDefaultKind,
    },
  });
}

export function sessionStopRecording(input: {
  sessionId: string;
  duration: number;
  isRecordingV2Enabled?: boolean;
  isTabSharing?: boolean;
}) {
  const session = reactQueryClient.getQueryData<SessionResponse>([
    GET_SESSION_BY_ID,
    { sessionId: input.sessionId },
  ]);
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionStopRecording,
      sessionId: input.sessionId,
      duration: input.duration,
      transcribeMode: session?.transcribe_mode,
      isRecordingV2Enabled: input.isRecordingV2Enabled,
      isTabSharing: input.isTabSharing,
    },
  });

  // V2 — Amplitude + Datadog only
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TranscriptionStopped,
    props: {
      session_id: input.sessionId,
      duration_seconds: input.duration,
      transcribe_mode: session?.transcribe_mode ?? undefined,
      is_recording_v2_enabled: input.isRecordingV2Enabled,
      is_tab_sharing: input.isTabSharing,
      demo_mode: getDemoModeForSession(input.sessionId),
    },
  });
}

export function sessionTemplateUpdated(input: { sessionId: string; templateId: string }) {
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionTemplateUpdated,
      templateId: input.templateId,
      sessionId: input.sessionId,
    },
  });
}

export function sessionViewed({ sessionId }: { sessionId: string }) {
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionEntered,
      sessionId: sessionId,
    },
  });

  track({
    options: { amplitude: false },
    event_name: TrackingEventName.SessionViewed,
    props: {
      session_id: sessionId,
    },
  });
}

export function sessionVoiceUpdated(input: {
  voiceStyle?: VoiceStyle;
  oldVoiceStyle?: VoiceStyle;
  sessionId: string;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionVoiceUpdated,
      voiceStyle: input.voiceStyle,
      oldVoiceStyle: input.oldVoiceStyle,
      sessionId: input.sessionId,
    },
  });
}

export function uploadRecording(input: { size: number; sessionId: string }) {
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionUploadRecording,
      fileSize: input.size,
      sessionId: input.sessionId,
    },
  });

  // V2 — Amplitude migration
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.AudioUploaded,
    props: {
      session_id: input.sessionId,
      upload_action: 'upload_recording',
      file_size: input.size,
    },
  });
}

export function uploadRecordingCancelled(size: number) {
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionUploadRecordingCancelled,
      error: 'upload cancelled',
      fileSize: size,
    },
  });

  // V2 — Amplitude migration
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.AudioStopped,
    props: {
      file_size: size,
    },
  });
}

export function uploadRecordingFailed(input: { size: number; sessionId: string }) {
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionUploadRecordingFailed,
      fileSize: input.size,
      sessionId: input.sessionId,
    },
  });

  // V2 — Amplitude migration
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.AudioFailed,
    props: {
      session_id: input.sessionId,
      file_size: input.size,
    },
  });
}

export function uploadRecordingSuccess(input: { size: number; sessionId: string }) {
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionUploadRecordingSuccess,
      fileSize: input.size,
      sessionId: input.sessionId,
    },
  });

  // V2 — Amplitude migration
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.AudioUploaded,
    props: {
      session_id: input.sessionId,
      upload_action: 'upload_recording_success',
      file_size: input.size,
    },
  });
}
